import {
  Modal,
  Plus,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  SnackbarStatus,
  Spinner,
  Typography,
  useSnackbar,
  ArrowIcon,
} from '@weareopenr/component-library';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useItems } from 'src/hooks/useItems/useItems';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { addItemsToMenu } from 'src/api/addItemsToMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import chefPlating from 'src/assets/images/chef-plating.png';
import styles from './SelectProductsModal.module.scss';
import { ItemType } from 'src/constants';
import { Range } from '@weareopenr/types';
import SelectableList from 'src/components/SelectableList/SelectableList';
import SelectableListWithInfiniteScroll from 'src/components/SelectableListWithInfiniteScroll/SelectableListWithInfiniteScroll';
import classnames from 'classnames/bind';
import { toMap } from 'src/helpers';

const cx = classnames.bind(styles);

interface ISelectProductsModal {
  open: boolean;
  closeModal: () => void;
  menuName: string;
  menuId: string;
  sectionId: string | null;
  onCreateNewProduct: () => void;
  menuRanges: Range[];
}

const getRangeSubtitle = (ranges: Range[]): string => {
  return ranges.length === 0 ? 'No Ranges' : ranges.map((range) => range.name).join(', ');
};

export const SelectProductsModal = ({
  open,
  closeModal,
  menuName,
  menuId,
  sectionId,
  onCreateNewProduct,
  menuRanges = [],
}: ISelectProductsModal) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Map<string, { id: string; label: string; subtitle: string }>
  >(new Map());
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSelectedExpanded, setIsSelectedExpanded] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const menuRangeIds = useMemo(() => menuRanges.map((range) => range.range_id), [menuRanges]);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useItems(
    debouncedSearchTerm,
    50,
    [ItemType.BUNDLE, ItemType.STANDARD, ItemType.NON_FOOD],
    undefined,
    menuRangeIds.length > 0 ? menuRangeIds : undefined,
  );

  const productOptions = useMemo(() => {
    if (!data?.pages) return [];

    return data.pages.flatMap((page) =>
      page.map((item) => ({
        id: item.itemId,
        label: item.name,
        subtitle: getRangeSubtitle(item.ranges) || 'No Ranges',
      })),
    );
  }, [data]);

  const productOptionsMap = useMemo(() => {
    return toMap(productOptions, 'id');
  }, [productOptions]);

  const selectedProductOptions = useMemo(() => {
    return Array.from(selectedProductsMap.values());
  }, [selectedProductsMap]);

  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedProducts(newSelectedIds);

    const newSelectedSet = new Set(newSelectedIds);
    const newMap = new Map(selectedProductsMap);

    newSelectedIds.forEach((id) => {
      if (!newMap.has(id)) {
        const product = productOptionsMap.get(id);
        if (product) {
          newMap.set(id, product);
        }
      }
    });

    Array.from(newMap.keys()).forEach((id) => {
      if (!newSelectedSet.has(id)) {
        newMap.delete(id);
      }
    });

    setSelectedProductsMap(newMap);
  };

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);

      const requestBody = selectedProducts.map((itemId) => ({
        item_id: itemId,
        section_id: sectionId ?? null,
      }));

      await addItemsToMenu(menuId, requestBody);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });

      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `Successfully added ${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''} to ${menuName}`,
      });

      return resetFields();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : 'Failed to add products to menu',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFields = () => {
    setSelectedProducts([]);
    setSelectedProductsMap(new Map());
    setSearchTerm('');
    closeModal();
  };

  return (
    <Modal open={open} closeModal={resetFields} image={chefPlating} imageAlt="chef plating">
      <div className={styles['modal-content']}>
        <Typography size="xxlarge">
          Add Product(s) to <span style={{ fontWeight: 'bold' }}>{menuName}</span>
        </Typography>

        <div className={styles['search-container']}>
          <SearchField
            className="select-products-modal-searchfield"
            placeholder="Search products..."
            onChange={(e) => setSearchTerm(e.target.value)}
            isDisabled={isSubmitting}
          />
          {selectedProducts.length === 0 && (
            <PrimaryButton
              icon={Plus}
              onClick={() => {
                resetFields();
                onCreateNewProduct();
              }}
              type="button"
            >
              Create New Product
            </PrimaryButton>
          )}
        </div>

        <div className={styles['products-scroll-container']}>
          {selectedProducts.length > 0 && (
            <div className={styles['selected-collapsible']}>
              <button
                className={styles['selected-header']}
                onClick={() => setIsSelectedExpanded((prev) => !prev)}
                type="button"
                aria-expanded={isSelectedExpanded}
              >
                <Typography size="medium" weight="semi-bold">
                  Currently Selected ({selectedProducts.length})
                </Typography>
                <div className={styles['arrow-button']}>
                  <ArrowIcon
                    className={cx('arrow', {
                      'arrow-down': !isSelectedExpanded,
                    })}
                  />
                </div>
              </button>
              {isSelectedExpanded && (
                <SelectableList
                  options={selectedProductOptions}
                  selectedOptions={selectedProducts}
                  onChange={handleSelectionChange}
                />
              )}
            </div>
          )}

          <div className={styles['product-library-section']}>
            <Typography size="medium" weight="bold">
              Product library:
            </Typography>

            {isLoading && (
              <div className={styles['spinner-container']}>
                <Spinner size="large" colour="rainbow" />
              </div>
            )}

            {!isLoading && productOptions.length === 0 && (
              <Typography className={styles['no-results']}>No results for your search.</Typography>
            )}

            {!isLoading && productOptions.length > 0 && (
              <SelectableListWithInfiniteScroll
                options={productOptions}
                selectedOptions={selectedProducts}
                onChange={handleSelectionChange}
                hasNextPage={hasNextPage}
                onLoadMore={fetchNextPage}
                isLoadingMore={isFetchingNextPage}
              />
            )}
          </div>
        </div>

        <ButtonContainer
          left={
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={resetFields}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0 || isSubmitting}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </Modal>
  );
};
